// Angular
import { Observable, debounceTime, distinctUntilChanged, finalize, map, of, startWith, takeUntil, tap } from 'rxjs';
import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

// Libraries

import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';

// Auth
import { AuthService } from '../..';
import { ConfirmPasswordValidator } from '../../../../shared/validators/confirm-password.validator';
import { CompanyService } from '../../../company/services/company.service';
import { CompanyRegisterModel } from '../../../company/models/company-view-model.model';
import { AppCleanup, LookUpResponse, TenantDto, UserModel } from '../../../../shared/models';

// Store
import { AuthActions } from '../../state/auth.action';
import { UserAuthActions } from '../../state/user-auth.action';

// Services
import { CoreService } from '../../../../shared/services';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Store
import { LookupState } from '../../../../shared/state/lookup.state';
import { LookupActions } from '../../../../shared/state/lookup.action';

// Helpers
import { FormHelper } from '../../../../shared/helpers';

// Environment
import { environment } from '../../../../../environments/environment';

// Enums
import { StatusType } from '../../../../shared/enums';

@Component({
	selector: 'register',
	templateUrl: './register.component.html',
	styleUrls: ['./register.component.scss'],
})
export class RegisterComponent extends AppCleanup implements OnInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	@Select(LookupState.isLoaded) isLoaded$: Observable<boolean>;
	@Select(LookupState.lookups) lookups$: Observable<LookUpResponse>;
	companies: CompanyRegisterModel[] = [];
	filteredCompanies: Observable<CompanyRegisterModel[]>;
	emailDomain: string = '';
	companyEmailDomains: string[] = [];
	recaptchaKey: string = '';
	form: FormGroup;
	tenants: TenantDto[] = [];
	showPassword: boolean = false;
	showConfirmPassword: boolean = false;

	constructor(
		private translateService: TranslateService,
		private router: Router,
		private auth: AuthService,
		private store: Store,
		private formBuilder: FormBuilder,
		private cdr: ChangeDetectorRef,
		private companyService: CompanyService,
		private spinnerService: SpinnerService,
		private route: ActivatedRoute,
		private coreService: CoreService
	) {
		super();
	}

	ngOnInit() {
		this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		this.recaptchaKey = environment.recaptchaKey;
		this.companyService
			.getCompaniesRegister()
			.pipe(takeUntil(this.destroy))
			.subscribe((companies: CompanyRegisterModel[]) => {
				this.companies = companies;
				this.companyEmailDomains = this.companies.map((company: CompanyRegisterModel) => company.emailDomain).filter((emailDomain: string) => !!emailDomain);
				this.checkCompaniesField();
				this.checkUrlParams();
				this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
			});

		this.isLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
			if (!isLoaded) {
				this.store.dispatch(new LookupActions.GetAll({ showSpinner: false, showToast: false }));
			}
		});

		this.lookups$.pipe(takeUntil(this.destroy)).subscribe((lookups) => {
			if (lookups) {
				this.tenants = lookups.tenants;
			}
		});

		this.initRegisterForm();
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}
	
	isLocalhost(): boolean {
		return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	}
	
	private initRegisterForm() {
		// Conditionally disable the recaptcha field if running from localhost;
		// this is for the automated testing of the Register form.
		const recaptchaValidators = this.isLocalhost() ? [] : [Validators.required];
		
		this.form = this.formBuilder.group(
			{
				tenantId: ['', Validators.compose([Validators.required])],
				firstName: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
				lastName: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
				email: [
					'',
					Validators.compose([
						Validators.required,
						Validators.email,
						Validators.minLength(3),
						// https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
						Validators.maxLength(320),
					]),
				],
				username: ['', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
				password: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(100)])],
				confirmPassword: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(100)])],
				companyName: ['', Validators.compose([Validators.required]), this.companyValidator.bind(this)],
				recaptchaReactive: [null, recaptchaValidators],
			},
			{
				validator: ConfirmPasswordValidator.MatchPassword,
			}
		);

		this.filteredCompanies = this.form.controls.companyName.valueChanges.pipe(
			startWith(''),
			map((value) => this.filterCompanies(value || ''))
		);

		this.form
			.get('email')
			.valueChanges.pipe(takeUntil(this.destroy))
			.subscribe((email: string) => {
				const emailDomain: string = email.split('@')[1];
				if (this.emailDomain !== emailDomain) {
					this.emailDomain = emailDomain;
					this.setSelectedCompany(this.emailDomain);
				}
			});

		this.checkCompaniesField();
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	companyValidator(control: FormControl) {
		if (control.value && this.form) {
			let index = this.companies.findIndex((company) => company.name === control.value);

			if (index > -1) {
				return of(null);
			}

			return of({ notSelected: true });
		}
	}

	filterCompanies(value: string): CompanyRegisterModel[] {
		const filterValue = value.toLowerCase();
		return this.companies.filter((company) => company.name.toLowerCase().includes(filterValue));
	}

	submit() {
		const controls = this.form.controls;

		// check form
		if (this.form.invalid) {
			Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
			return;
		}

		this.spinnerService.showAppSpinner(); // Turns on app wide spinner

		const user: UserModel = new UserModel();
		user.email = controls.email.value;
		user.username = controls.username.value;
		user.firstName = controls.firstName.value;
		user.lastName = controls.lastName.value;
		user.password = controls.password.value;
		user.tenantId = controls.tenantId.value;
		user.companyIds = [this.companies.find((company: CompanyRegisterModel) => company.name.toLowerCase() == controls.companyName.value.toLowerCase()).id];
		user.roles = [];

		this.auth
			.register(user)
			.pipe(
				tap((response) => {
					if (response.errors.length > 0) {
						let errorMessage = '';

						response.errors.forEach((e) => {
							errorMessage += `${e.description}. `;
						});

						this.coreService.toast({ message: errorMessage, type: StatusType.DANGER });
					} else if (response.user) {
						this.store.dispatch(new AuthActions.SetToken(response.user.accessToken));
						this.store.dispatch(new UserAuthActions.SetUser(response.user));

						// pass notice message to the login page
						const message = this.translateService.instant('MODULE.AUTH.REGISTER.SUCCESS');
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						this.router.navigateByUrl('/auth/login');
					}
				}),
				takeUntil(this.destroy),
				finalize(() => {
					this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					this.cdr.markForCheck();
				})
			)
			.subscribe();
	}

	private checkCompaniesField() {
		const control: AbstractControl = this.form.controls['companyName'];
		this.companies?.length > 0 ? control.enable() : control.disable();
	}

	private setSelectedCompany(emailDomain: string) {
		// If the user's entered Email matches
		if (emailDomain && this.companyEmailDomains.includes(emailDomain)) {
			// Find company by emailDomain property.
			const company: CompanyRegisterModel = this.companies.find((company: CompanyRegisterModel) => company.emailDomain === emailDomain);

			// Set selected company associated with the given company.
			this.form.controls['companyName'].setValue(company.name);
		}
	}

	private checkUrlParams() {
		const controls = this.form.controls;
		const params: { email?: string; company?: string } = this.route.snapshot.queryParams;

		// If 'company' (id) in URL params, set the company form field value.
		if (params?.company) {
			const companyId: number = parseInt(params.company);
			const companyName: string = this.companies.find((company: CompanyRegisterModel) => company.id === companyId)?.name;

			controls.companyName.setValue(companyName);
		}

		// If 'email' in URL params, set the email form field value.
		if (params?.email) {
			controls.email.setValue(params.email);
		}
	}
}
